import { z } from 'zod';
import { FIDELIDADE_PLANO, FREQUENCIA_PLANO, } from '../dominio/index.js';
import { adicionarDias, diasEntre } from '../tempo/index.js';
import { zData } from './agenda.js';
import { adicionarMeses, diasNoMes, mesDe, zMes } from './financeiro.js';
import { camposDeModalidade, conferirModalidade } from './modalidades.js';
import { zId } from './primitivos.js';
/**
 * Os contratos de planos e faturas — `#v-planos`, `#m-plano`,
 * `#m-pagamento`, `#m-reajuste`, a tabela "Cobranças" de `#v-dash`, a aba
 * Financeiro de `#m-aluno` e o drill-down de inadimplência de `#m-kpi`.
 *
 * Quatro regras moram aqui porque os dois lados precisam delas iguais:
 *
 *   quando a fatura nasce      `faturaDevida` — o job gera por ela, e a
 *                              tela de assinatura mostra "primeira fatura:
 *                              05/10" pela mesma conta
 *   quanto custa o atraso      `encargosDaFatura` — o servidor cobra, e
 *                              `#m-pagamento` mostra o total antes de
 *                              confirmar, sem ida ao servidor a cada data
 *   o que o reajuste faz       `valorReajustado` — a simulação e a
 *                              aplicação são a mesma função
 *   quando a régua avisa       `eventosDevidos`
 *
 * E as três de sempre: dinheiro em centavos, data de CALENDÁRIO da arena
 * ("2026-09-05") e `Vencida` como estado DERIVADO — nunca gravado.
 *
 * Ver: Arena_backend/Docs/plano/12-planos-faturas.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/** Descontinuado não aceita assinatura nova; as que existem seguem cobrando. */
export const STATUS_PLANO = ['Ativo', 'Descontinuado'];
export const ROTULO_FREQUENCIA_PLANO = {
    UmaVez: '1x por semana',
    DuasVezes: '2x por semana',
    TresVezes: '3x por semana',
    Ilimitado: 'Ilimitado',
};
export const ROTULO_FIDELIDADE_PLANO = {
    Mensal: 'Mensal',
    Trimestral: 'Trimestral',
    Semestral: 'Semestral',
};
/**
 * "% ou valor fixo · indicação, plano família" — o campo Desconto de
 * `#m-plano`. O percentual é em CENTÉSIMOS de ponto (1000 = 10%), como o
 * do reajuste: "4,62%" também é um número inteiro.
 */
export const TIPOS_DE_DESCONTO = ['Percentual', 'Valor'];
/** O que a coluna guarda. `Vencida` fica de fora: é derivada. */
export const SITUACOES_GRAVADAS_DA_FATURA = [
    'EmAberto',
    'Paga',
    'Cancelada',
];
export const ROTULO_SITUACAO_FATURA = {
    EmAberto: 'Em aberto',
    Paga: 'Paga',
    Vencida: 'Vencida',
    Cancelada: 'Cancelada',
};
/**
 * As formas de `#m-pagamento` — Pix, Dinheiro, Cartão — e o boleto, que a
 * recepção também recebe. "Mensalidade" é forma da COMANDA lançada na
 * fatura (documento 13), não de quem paga a fatura.
 */
export const FORMAS_DO_RECEBIMENTO = [
    'Pix',
    'Dinheiro',
    'Cartao',
    'Boleto',
];
/**
 * Os quatro eventos da régua — o contrato com o documento 15, que os
 * transforma em mensagem. Os nomes são os do documento 12, literais.
 */
export const EVENTOS_DA_REGUA = [
    'fatura.a_vencer',
    'fatura.vence_hoje',
    'fatura.vencida',
    'fatura.paga',
];
/** Os filtros de "Faturas" em `#v-planos`: Todas, Vencidas, A vencer, Pagas. */
export const FILTROS_DE_FATURA = ['todas', 'vencidas', 'a_vencer', 'pagas'];
/** De onde veio cada valor de um contrato no tempo. */
export const ORIGENS_DO_VALOR = ['Contratacao', 'Alteracao', 'Reajuste'];
// ---------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------
/** "O lembrete sai três dias antes, no dia e depois do vencimento." */
export const DIAS_DO_LEMBRETE_ANTES = 3;
/**
 * A antecedência MÍNIMA com que a fatura existe antes do vencimento — ver
 * `faturaDevida`. Três dias do lembrete e mais dois de folga: a madrugada
 * em que o job falhou e a retentativa não cobriu não pode custar o aviso.
 */
export const ANTECEDENCIA_DA_FATURA = 5;
/**
 * "1% ao mês" é juro simples pro rata sobre um mês COMERCIAL de trinta
 * dias — a convenção do boleto: 0,033% ao dia.
 */
export const DIAS_DO_MES_COMERCIAL = 30;
export const DIA_DE_VENCIMENTO_PADRAO = 5;
export const PESSOAS_POR_ASSINATURA_MAXIMO = 6;
/** R$ 100 mil por mês: pega o zero a mais antes de ele virar receita recorrente. */
export const VALOR_MAXIMO_DO_PLANO = 10_000_000;
/** Em centésimos de ponto: de −50% a +100%. */
export const PERCENTUAL_MINIMO_DE_REAJUSTE = -5000;
export const PERCENTUAL_MAXIMO_DE_REAJUSTE = 10000;
/** O drill-down de `#m-kpi`: "Últimos 6 meses" e "3 maiores". */
export const MESES_NA_SERIE_DE_INADIMPLENCIA = 6;
export const MAIORES_DEVEDORES = 3;
// ---------------------------------------------------------------------
// Regras — o calendário da fatura
// ---------------------------------------------------------------------
const dois = (n) => String(n).padStart(2, '0');
/**
 * O vencimento de uma competência: o dia do contrato — ou o último dia do
 * mês, nos meses em que o dia não existe. O contrato do dia 31 vence em
 * 28 de fevereiro e volta a 31 em março: cada mês é calculado do dia do
 * contrato, nunca do vencimento anterior.
 */
export function vencimentoNaCompetencia(competencia, dia) {
    return `${competencia}-${dois(Math.min(dia, diasNoMes(competencia)))}`;
}
/**
 * Em aberto com vencimento antes de hoje é `Vencida` — derivado, nunca
 * gravado: "estado calculado nunca fica velho". No próprio dia do
 * vencimento a fatura ainda está em aberto.
 */
export function situacaoDaFatura(situacao, vencimento, hoje) {
    if (situacao !== 'EmAberto')
        return situacao;
    return vencimento < hoje ? 'Vencida' : 'EmAberto';
}
/** Dias corridos depois do vencimento. Pagar antes ou no dia é zero. */
export function diasDeAtraso(vencimento, data) {
    return Math.max(0, diasEntre(vencimento, data));
}
/**
 * As competências que a rotina de um dia olha: a anterior, a corrente e a
 * seguinte. A anterior recupera a fatura que devia ter nascido e não
 * nasceu — o job fora do ar na virada do mês, o contrato registrado com
 * início retroativo. Mais para trás que isso não é cobrança automática, é
 * acerto de contas, e fica com uma pessoa.
 */
export function competenciasDaRotina(hoje) {
    const mes = mesDe(hoje);
    return [adicionarMeses(mes, -1), mes, adicionarMeses(mes, 1)];
}
/**
 * A fatura desta competência já deve existir em `hoje`? Devolve o
 * vencimento dela, ou `null`.
 *
 * Não existe fatura com vencimento antes do início do contrato nem a
 * partir do fim dele. E ela NASCE no primeiro dia do mês da competência —
 * "A receber no mês" é a soma do mês inteiro desde o dia 1, como o
 * protótipo mostra em 2 de setembro com as 324 faturas de setembro —, ou
 * `ANTECEDENCIA_DA_FATURA` dias antes do vencimento, o que vier primeiro:
 * a fatura do dia 2 de outubro não pode nascer no dia 1º, depois da hora
 * do lembrete de três dias antes.
 */
export function faturaDevida(assinatura, competencia, hoje) {
    const vencimento = vencimentoNaCompetencia(competencia, assinatura.diaVencimento);
    if (vencimento < assinatura.inicio)
        return null;
    if (assinatura.fim !== null && vencimento >= assinatura.fim)
        return null;
    const nasceu = `${competencia}-01` <= hoje || adicionarDias(vencimento, -ANTECEDENCIA_DA_FATURA) <= hoje;
    return nasceu ? vencimento : null;
}
/**
 * A primeira fatura de um contrato: a primeira competência cujo vencimento
 * cai a partir do início. Quem assina no dia 20 com vencimento no dia 5
 * paga a primeira no dia 5 do mês seguinte — sem proporcional, que é
 * pergunta em aberto do documento.
 */
export function primeiraFatura(inicio, diaVencimento) {
    const mes = mesDe(inicio);
    const noMes = vencimentoNaCompetencia(mes, diaVencimento);
    if (noMes >= inicio)
        return { competencia: mes, vencimento: noMes };
    const seguinte = adicionarMeses(mes, 1);
    return { competencia: seguinte, vencimento: vencimentoNaCompetencia(seguinte, diaVencimento) };
}
/**
 * O primeiro vencimento em ou depois de `hoje`, no dia do contrato (ou no
 * último dia dos meses curtos). É o dia em que a próxima fatura vence —
 * exista ela já ou não.
 */
export function proximoVencimento(diaVencimento, hoje) {
    const mes = mesDe(hoje);
    const noMes = vencimentoNaCompetencia(mes, diaVencimento);
    if (noMes >= hoje)
        return noMes;
    return vencimentoNaCompetencia(adicionarMeses(mes, 1), diaVencimento);
}
/**
 * Esta assinatura ainda vai gerar fatura para receber o consumo do bar?
 *
 * Sem `fim`, sempre. Com `fim` (semiaberto: o primeiro dia sem contrato),
 * só se o próximo vencimento — contado do início, quando ele ainda está no
 * futuro — cai antes dele. É a pergunta que separa "fica pendente e entra na
 * próxima fatura" de "não há fatura nenhuma a caminho: feche de outro jeito".
 */
export function aindaVaiFaturar(assinatura, hoje) {
    if (assinatura.fim === null)
        return true;
    const base = hoje > assinatura.inicio ? hoje : assinatura.inicio;
    return proximoVencimento(assinatura.diaVencimento, base) < assinatura.fim;
}
/**
 * A mensalidade de uma fatura: o `valor` é o TOTAL (mensalidade + bar), e o
 * `consumo` é a parte do bar. Multa e juros incidem só sobre isto — o bar
 * que o aluno consumiu não rende encargo —, então todo `encargosDaFatura`
 * recebe `mensalidadeDa(fatura)`, nunca o total.
 */
export function mensalidadeDa(fatura) {
    return fatura.valor - fatura.consumo;
}
/**
 * Multa e juros no instante do recebimento, sobre os dias de atraso até a
 * DATA DO RECEBIMENTO — não até hoje: o Pix de ontem registrado hoje não
 * paga um dia a mais.
 *
 * Multa: uma vez, sobre o valor ("aplicada uma vez sobre a fatura
 * vencida", diz a configuração). Juros: simples, pro rata dia sobre um
 * mês de trinta dias. Os dois sobre o valor original, e arredondados ao
 * centavo cada um — é o que aparece separado em `#m-pagamento`.
 *
 * Uma fatura de R$ 210 vencida há 12 dias, com 2% e 1% ao mês: R$ 4,20
 * de multa, R$ 0,84 de juros, R$ 215,04. (O R$ 6,30 do protótipo cobraria
 * o mês inteiro de juros por 12 dias — a configuração diz "proporcional
 * aos dias de atraso".)
 */
export function encargosDaFatura(valor, vencimento, dataDoRecebimento, regra) {
    const dias = diasDeAtraso(vencimento, dataDoRecebimento);
    if (dias === 0)
        return { diasDeAtraso: 0, multa: 0, juros: 0, encargos: 0, total: valor };
    const multa = Math.round((valor * regra.multaPercentual) / 100);
    const juros = Math.round((valor * regra.jurosMesPercentual * dias) / (100 * DIAS_DO_MES_COMERCIAL));
    return {
        diasDeAtraso: dias,
        multa,
        juros,
        encargos: multa + juros,
        total: valor + multa + juros,
    };
}
/** O valor com o desconto do plano aplicado. Pode chegar a zero ou menos: quem chama recusa. */
export function valorComDesconto(valor, desconto) {
    if (!desconto)
        return valor;
    const abatimento = desconto.tipo === 'Percentual' ? Math.round((valor * desconto.valor) / 10_000) : desconto.valor;
    return valor - abatimento;
}
/**
 * O reajuste de um valor: `percentual` em centésimos de ponto. R$ 160 com
 * 6% é R$ 169,60 — a conta exata de `#m-reajuste`, ao centavo.
 */
export function valorReajustado(valor, percentual) {
    return Math.round((valor * (10_000 + percentual)) / 10_000);
}
/** 600 → "6%"; 462 → "4,62%"; −250 → "−2,5%". */
export function textoDoPercentual(centesimos) {
    const numero = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Math.abs(centesimos) / 100);
    return `${centesimos < 0 ? '−' : ''}${numero}%`;
}
/**
 * O valor do contrato em vigor numa data: a linha mais recente que já
 * começou. `null` antes da primeira — um contrato não tem preço antes de
 * existir.
 */
export function valorEm(valores, data) {
    let vigente = null;
    for (const linha of valores) {
        if (linha.vigenteDesde <= data && (!vigente || linha.vigenteDesde > vigente.vigenteDesde)) {
            vigente = linha;
        }
    }
    return vigente?.valor ?? null;
}
/**
 * O valor de TABELA de um plano numa data: o gravado, com os reajustes
 * ainda não incorporados que já valem nela, na ordem da vigência. É o que
 * faz o plano custar o novo valor a partir de 01/11 mesmo que o job da
 * madrugada ainda não tenha gravado a conta.
 */
export function valorDeTabelaEm(valor, pendentes, data) {
    return [...pendentes]
        .filter((reajuste) => reajuste.vigencia <= data)
        .sort((a, b) => a.vigencia.localeCompare(b.vigencia))
        .reduce((atual, reajuste) => valorReajustado(atual, reajuste.percentual), valor);
}
/**
 * Os eventos que uma fatura EM ABERTO deve ter publicados em `hoje`.
 *
 *   a_vencer     de três dias antes até a véspera; a referência é o dia
 *                "três dias antes". A fatura que nasceu em cima da hora
 *                ainda avisa — e avisa uma vez só.
 *   vence_hoje   só no dia. Não se diz "vence hoje" amanhã.
 *   vencida      a cadência da configuração da arena — dias depois do
 *                vencimento. Publica o passo MAIS RECENTE que já chegou,
 *                e só ele: o job que ficou dois dias fora não despeja três
 *                cobranças de uma vez.
 *
 * A chave `(fatura, tipo, referencia)` é única no banco: rodar a régua duas
 * vezes no mesmo dia não publica duas vezes.
 */
export function eventosDevidos(vencimento, hoje, diasDepoisDoVencimento) {
    const eventos = [];
    const antes = adicionarDias(vencimento, -DIAS_DO_LEMBRETE_ANTES);
    if (hoje >= antes && hoje < vencimento) {
        eventos.push({ tipo: 'fatura.a_vencer', referencia: antes });
    }
    if (hoje === vencimento) {
        eventos.push({ tipo: 'fatura.vence_hoje', referencia: vencimento });
    }
    if (hoje > vencimento) {
        const passos = [...diasDepoisDoVencimento]
            .sort((a, b) => a - b)
            .map((dias) => adicionarDias(vencimento, dias))
            .filter((dia) => dia <= hoje);
        const ultimo = passos[passos.length - 1];
        if (ultimo)
            eventos.push({ tipo: 'fatura.vencida', referencia: ultimo });
    }
    return eventos;
}
// ---------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------
const texto = (minimo, maximo, mensagem) => z
    .string({ required_error: mensagem })
    .trim()
    .min(minimo, mensagem)
    .max(maximo, `No máximo ${maximo} caracteres.`);
export const zDiaDeVencimento = z
    .number({ invalid_type_error: 'Informe o dia do vencimento.' })
    .int('O dia do vencimento é um número inteiro.')
    .min(1, 'O dia do vencimento vai de 1 a 31.')
    .max(31, 'O dia do vencimento vai de 1 a 31.');
export const zValorMensal = z
    .number({ invalid_type_error: 'Informe o valor em centavos.' })
    .int('O valor é em centavos, sem fração.')
    .positive('O valor precisa ser maior que zero.')
    .max(VALOR_MAXIMO_DO_PLANO, 'Valor acima de R$ 100 mil por mês — confira os zeros.');
export const zDescontoDoPlano = z
    .object({
    tipo: z.enum(TIPOS_DE_DESCONTO, {
        errorMap: () => ({ message: 'Desconto em percentual ou em valor.' }),
    }),
    valor: z
        .number({ invalid_type_error: 'Informe o desconto.' })
        .int('O desconto é um número inteiro — centésimos de ponto ou centavos.')
        .positive('O desconto precisa ser maior que zero.'),
})
    .superRefine((desconto, ctx) => {
    if (desconto.tipo === 'Percentual' && desconto.valor > 10_000) {
        ctx.addIssue({ code: 'custom', path: ['valor'], message: 'Desconto acima de 100%.' });
    }
    if (desconto.tipo === 'Valor' && desconto.valor > VALOR_MAXIMO_DO_PLANO) {
        ctx.addIssue({ code: 'custom', path: ['valor'], message: 'Desconto acima do valor máximo.' });
    }
});
/** "3 dias antes", "No dia", "Após o vencimento" — as três opções de `#m-plano`. */
export const zLembretesDoPlano = z.object({
    antes: z.boolean(),
    noDia: z.boolean(),
    depois: z.boolean(),
});
export const zPessoasPorAssinatura = z
    .number({ invalid_type_error: 'Informe quantas pessoas o plano cobre.' })
    .int()
    .min(1, 'O plano cobre ao menos uma pessoa.')
    .max(PESSOAS_POR_ASSINATURA_MAXIMO, `No máximo ${PESSOAS_POR_ASSINATURA_MAXIMO} pessoas.`);
/** Centésimos de ponto: 600 é 6%. */
export const zPercentualDeReajuste = z
    .number({ invalid_type_error: 'Informe o percentual.' })
    .int('O percentual vai em centésimos de ponto — 6% é 600.')
    .min(PERCENTUAL_MINIMO_DE_REAJUSTE, 'Redução de no máximo 50%.')
    .max(PERCENTUAL_MAXIMO_DE_REAJUSTE, 'Reajuste de no máximo 100%.')
    .refine((percentual) => percentual !== 0, 'Um reajuste de zero não muda nada.');
// ---------------------------------------------------------------------
// Entradas
// ---------------------------------------------------------------------
const camposDoPlano = {
    nome: texto(2, 80, 'O plano precisa de um nome.'),
    ...camposDeModalidade,
    frequencia: z.enum(FREQUENCIA_PLANO, {
        errorMap: () => ({ message: 'Escolha a frequência.' }),
    }),
    valor: zValorMensal,
    fidelidade: z.enum(FIDELIDADE_PLANO, {
        errorMap: () => ({ message: 'Mensal, trimestral ou semestral.' }),
    }),
    diaVencimento: zDiaDeVencimento,
    pessoas: zPessoasPorAssinatura,
    desconto: zDescontoDoPlano.nullable(),
    status: z.enum(STATUS_PLANO),
    lembretes: zLembretesDoPlano,
};
function descontoCabeNoValor(plano, ctx) {
    if (plano.valor === undefined || !plano.desconto)
        return;
    if (valorComDesconto(plano.valor, plano.desconto) <= 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['desconto'],
            message: 'O desconto não pode zerar a mensalidade.',
        });
    }
}
/**
 * O corpo de `POST /planos` — `#m-plano`.
 *
 * Valor e dia de vencimento são os de TABELA: o que uma assinatura nova
 * recebe. Quem já assinou mantém o próprio contrato — mudar o valor dele é
 * reajuste, com vigência.
 */
export const zNovoPlano = z
    .object({
    ...camposDoPlano,
    pessoas: zPessoasPorAssinatura.default(1),
    desconto: zDescontoDoPlano.nullable().default(null),
    status: z.enum(STATUS_PLANO).default('Ativo'),
    lembretes: zLembretesDoPlano.default({ antes: true, noDia: true, depois: true }),
})
    .superRefine(descontoCabeNoValor)
    .superRefine(conferirModalidade);
/** `PATCH /planos/:id`. O servidor confere o desconto contra o valor gravado. */
export const zAlteracaoDePlano = z
    .object(camposDoPlano)
    .partial()
    .superRefine(descontoCabeNoValor)
    .superRefine(conferirModalidade)
    .refine((a) => Object.values(a).some((v) => v !== undefined), { message: 'Nada para alterar.' });
/**
 * O corpo de `POST /planos/assinaturas` — "aplicar" o plano a alunos, que
 * é o que a Gestão pode ("Ver e aplicar").
 *
 * O PRIMEIRO aluno é o titular. O valor vem do plano, com o desconto dele
 * se `aplicarDesconto`; informar outro valor é negociar preço, e o
 * servidor exige para isso a ação de editar planos.
 */
export const zNovaAssinatura = z.object({
    planoId: zId,
    alunoIds: z
        .array(zId)
        .min(1, 'Escolha quem a assinatura cobre.')
        .max(PESSOAS_POR_ASSINATURA_MAXIMO, `No máximo ${PESSOAS_POR_ASSINATURA_MAXIMO} pessoas.`)
        .refine((ids) => new Set(ids).size === ids.length, 'Cada aluno aparece uma vez.'),
    inicio: zData,
    diaVencimento: zDiaDeVencimento.optional(),
    aplicarDesconto: z.boolean().default(false),
    valor: zValorMensal.optional(),
    observacao: z.string().trim().max(200, 'No máximo 200 caracteres.').optional(),
});
/**
 * `PATCH /planos/assinaturas/:id`. O novo valor vale A PARTIR de uma data:
 * as faturas já geradas não mudam, e as que nascerem depois dela leem o
 * valor novo.
 */
export const zAlteracaoDeAssinatura = z
    .object({
    diaVencimento: zDiaDeVencimento.optional(),
    novoValor: z.object({ valor: zValorMensal, aPartirDe: zData }).optional(),
    observacao: z.string().trim().max(200, 'No máximo 200 caracteres.').nullable().optional(),
})
    .refine((a) => Object.values(a).some((v) => v !== undefined), { message: 'Nada para alterar.' });
/** Encerrar — o `fim` é o primeiro dia sem contrato. */
export const zEncerramentoDeAssinatura = z.object({
    fim: zData,
    motivo: texto(3, 200, 'Diga por que a assinatura está sendo encerrada.'),
});
/**
 * `#m-pagamento`. `totalEsperado` é o total que a tela mostrou: se a
 * configuração de multa mudou entre abrir o modal e confirmar, o servidor
 * recusa em vez de cobrar um número que ninguém viu.
 */
export const zRecebimento = z.object({
    data: zData,
    formaPagamento: z.enum(FORMAS_DO_RECEBIMENTO, {
        errorMap: () => ({ message: 'Pix, dinheiro, cartão ou boleto.' }),
    }),
    cobrarEncargos: z.boolean({ required_error: 'Diga se cobra multa e juros.' }),
    totalEsperado: z.number().int().nonnegative().optional(),
});
export const zCancelamentoDeFatura = z.object({
    motivo: texto(3, 200, 'Diga por que a fatura está sendo cancelada.'),
});
/** Desfazer um recebimento registrado por engano: o razão estorna na origem. */
export const zEstornoDeRecebimento = z.object({
    motivo: texto(3, 200, 'Diga por que o recebimento está sendo desfeito.'),
});
/**
 * `#m-reajuste` — simular e aplicar recebem o MESMO corpo: é o que faz a
 * simulação bater com o impacto real. `planoId` nulo é "Todos os planos".
 */
export const zReajuste = z.object({
    planoId: zId.nullable(),
    percentual: zPercentualDeReajuste,
    vigencia: zData,
});
/** A página de faturas. O cursor é opaco, como o do extrato. */
export const zConsultaDeFaturas = z.object({
    filtro: z.enum(FILTROS_DE_FATURA).default('todas'),
    mes: zMes.optional(),
    cursor: z.string().max(200).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=planos.js.map